/**
 * The desktop aim/gait/crouch control scheme (character-animation-runtime.md
 * §3.7), extracted so BOTH free-roam views share one implementation.
 *
 * It was originally written inline in `index.ts` (the settlement). The
 * location view — an equally free-roam view with the same camera, sim and
 * rigged player — never got it, so it fed the animator literal constants
 * (`gait: 'run', crouched: false, aiming: false`), which made walk, crouch
 * and aim animations unreachable *by construction* there: not a tuning gap
 * but three whole animation states that could never play, in half the game's
 * views. Copying the wiring would have been a second implementation of a
 * scheme whose whole difficulty is the cancel/reset discipline
 * (controls-layout.md rule 7 — "no input state may persist across a cancel"),
 * which is exactly the sort of thing that drifts between copies.
 *
 * Scope is deliberately just the *state* this scheme owns — `aiming`/`gait`/
 * `crouched`/`aimYaw` — plus the listeners that can only be about aiming
 * (RMB, and the three cancel paths). Camera-drag, click-to-interact and
 * attack stay in each view's own pointer handlers, which read `aiming` here to
 * stay mutually exclusive with it. That keeps the extraction a move rather
 * than a redesign of `index.ts`'s existing pointer logic.
 */
import type * as THREE from 'three';
import type { Picker } from './render/picking';
import { keyName } from './key-input';

export interface PlayerControlsOptions {
  /** Element the view's listeners live on (also the pointer-capture target). */
  container: HTMLElement;
  /**
   * The canvas itself. Only a press that starts here enters aim mode —
   * `container` also wraps the DOM overlays (dialog, paperdoll, vendor
   * panel), and treating a press on those as an aim press would both aim and
   * fight those panels' own click handling.
   */
  canvas: HTMLElement;
  picker: Picker;
  /** Read lazily (not captured) — `CameraController` swaps nothing, but this keeps the module from holding a stale reference. */
  getCamera: () => THREE.Camera;
  /**
   * True while a modal UI owns the keyboard (dialog / vendor panel). Blocks
   * the crouch and gait toggles so typing/choosing in a panel can't silently
   * change movement state behind it. Aim entry is already canvas-gated, so it
   * doesn't need this.
   */
  isInputBlocked?: () => boolean;
  /**
   * Whether RMB may enter aim mode at all. Default true.
   *
   * `false` makes this a movement-only scheme: `aiming` is pinned false, so
   * every consumer that reads it — the aim arc, the LMB/Space attack paths,
   * the animator's aim branch — goes quiet without any of them needing to
   * know why. The settlement passes `false` (Janeks, 2026-09-19: *"we need
   * to disable aiming and shooting at home settlement"*); it is a safe zone
   * with nothing to shoot, and offering a weapon-ready stance there promised
   * combat the view does not have.
   *
   * The context-menu suppression stays on regardless — RMB doing nothing is
   * the intent, RMB popping the browser's menu over the game is not.
   */
  allowAim?: boolean;
}

export interface PlayerControls {
  /** Capslock-toggled persistent gait. */
  readonly gait: 'walk' | 'run';
  /** `c`-toggled persistent crouch, independent of gait. */
  readonly crouched: boolean;
  /** True while RMB is held (see the cancel paths in `dispose`'s listeners). */
  readonly aiming: boolean;
  /** Last ground-plane yaw from the character to the cursor, or null if never resolved. */
  readonly aimYaw: number | null;
  /** What `BaseSim.update` wants for `aimYaw` — the live value while aiming, null otherwise. */
  readonly simAimYaw: number | null;
  /**
   * Recomputes `aimYaw` from the cursor against the ground plane. Call once
   * per frame BEFORE `sim.update()`, passing the player position the sim
   * currently reports (one frame stale by construction — this frame's
   * position isn't known until the sim runs; imperceptible at 60fps).
   * A ray that misses the ground plane holds the previous `aimYaw` rather
   * than snapping facing to a default.
   */
  updateAim(playerPos: { x: number; z: number }): void;
  /**
   * Feed the view's own `keydown` here. Not a `window` listener of its own:
   * each view's keydown already has ordering concerns this must not jump
   * ahead of (`index.ts` gives the dialog first refusal on every key).
   */
  handleKeyDown(e: KeyboardEvent): void;
  /**
   * Feed the view's own `keyup` here, for the same reason as `handleKeyDown`.
   *
   * Only the gait re-sync listens to it, and it is not optional: on macOS the
   * CapsLock key fires a `keydown` when the lock ENGAGES and only a `keyup`
   * when it RELEASES, so a view that wires the keydown alone can turn walking
   * on and never off. See `syncGait`.
   */
  handleKeyUp(e: KeyboardEvent): void;
  dispose(): void;
}

export function createPlayerControls(opts: PlayerControlsOptions): PlayerControls {
  const { container, canvas, picker, getCamera, isInputBlocked } = opts;
  const allowAim = opts.allowAim ?? true;

  let aiming = false;
  let gait: 'walk' | 'run' = 'run';
  let crouched = false;
  let aimYaw: number | null = null;
  let lastMouseX = 0;
  let lastMouseY = 0;

  const blocked = (): boolean => isInputBlocked?.() ?? false;

  const onPointerDown = (e: PointerEvent): void => {
    if (e.target !== canvas) return;
    lastMouseX = e.clientX;
    lastMouseY = e.clientY;
    // RMB enters aim mode, and deliberately never captures the pointer —
    // see `onWindowPointerUp` for why release is handled on `window`.
    if (e.button === 2 && allowAim) aiming = true;
  };

  const onPointerMove = (e: PointerEvent): void => {
    lastMouseX = e.clientX;
    lastMouseY = e.clientY;
  };

  /**
   * RMB release is caught on `window`, not `container`, and NOT via pointer
   * capture (`onPointerDown` never captures). Dragging the cursor off
   * `container` before letting go would otherwise never produce a pointerup
   * the container sees, leaving `aiming` stuck true forever — precisely the
   * failure controls-layout.md rule 7 exists to forbid.
   */
  const onWindowPointerUp = (e: PointerEvent): void => {
    if (e.button === 2) aiming = false;
  };
  /** OS-level interruption, and the window losing focus while RMB is held — same rule. */
  const onPointerCancel = (): void => { aiming = false; };
  const onWindowBlur = (): void => { aiming = false; };
  const onContextMenu = (e: MouseEvent): void => { e.preventDefault(); };

  /**
   * Gait from the OS's live CapsLock lock state, off ANY key event.
   *
   * ─── The asymmetry this fixes ───────────────────────────────────────────
   * Reported 2026-09-18: *"Run→Walk on button press changes right away,
   * Walk→Run — the icon changes only when start moving, not when button is
   * pressed."*
   *
   * macOS does not fire a keydown/keyup pair for CapsLock the way an ordinary
   * key does. Pressing it to ENGAGE the lock fires `keydown`; pressing it
   * again to RELEASE the lock fires only `keyup`. So a re-sync driven by
   * keydown alone sees exactly one of the two transitions:
   *
   *   run → walk   keydown arrives, state reads true   → walk, immediately
   *   walk → run   NO keydown at all                   → still walk
   *
   * and the player stayed walking until the next unrelated keydown happened
   * to re-sync it — which in practice was pressing W, hence "the icon changes
   * only when I start moving". The HUD was never at fault: it repaints from
   * `controls.gait` every frame and diffs internally, so it was showing the
   * state it was given.
   *
   * Driving this from keyup as well as keydown covers both transitions on
   * every platform, and costs nothing where both events already fire
   * (Windows/Linux): the two agree on the settled state, and assigning the
   * value `gait` already holds is a no-op.
   *
   * Still a READ of existing OS state rather than a toggle — never `gait =
   * gait === 'run' ? 'walk' : 'run'`. A toggle drifts out of step with the
   * actual lock the first time an event is missed (alt-tabbing away, pressing
   * CapsLock in another window, the browser swallowing a key) and then has no
   * way back; a read self-corrects on the very next key.
   */
  const syncGait = (e: KeyboardEvent): void => {
    gait = e.getModifierState('CapsLock') ? 'walk' : 'run';
  };

  container.addEventListener('pointerdown', onPointerDown);
  container.addEventListener('pointermove', onPointerMove);
  container.addEventListener('pointercancel', onPointerCancel);
  container.addEventListener('contextmenu', onContextMenu);
  window.addEventListener('pointerup', onWindowPointerUp);
  window.addEventListener('blur', onWindowBlur);

  return {
    get gait() { return gait; },
    get crouched() { return crouched; },
    get aiming() { return aiming; },
    get aimYaw() { return aimYaw; },
    get simAimYaw() { return aiming ? aimYaw : null; },

    updateAim(playerPos) {
      if (!aiming) return;
      const hit = picker.pickGround(lastMouseX, lastMouseY, container, getCamera());
      if (hit) aimYaw = Math.atan2(hit.x - playerPos.x, hit.z - playerPos.z);
    },

    handleKeyDown(e) {
      // Persistent toggle, not held — `!e.repeat` so holding the key doesn't
      // rapid-flip it. `blocked()` gates the crouch toggle ONLY: the gait read
      // below must stay unconditional (see its comment).
      if (keyName(e) === 'c' && !e.repeat && !blocked()) crouched = !crouched;
      // Re-synced from the OS's actual CapsLock lock state on every non-repeat
      // keydown, deliberately NOT gated on `e.key === 'CapsLock'`: some
      // platforms don't reliably fire a keydown for both CapsLock
      // transitions, and keying off the literal event risks repeat-driven
      // flicker. Reading `getModifierState` here routes around both — if the
      // CapsLock event itself is ever missed, the very next keypress
      // self-corrects. Assigning the value it already holds is a no-op.
      //
      // **Deliberately NOT behind `blocked()`** — the self-correction above is
      // the whole point, and a dialog/vendor panel is exactly when a CapsLock
      // press gets missed. Gating it there would let the HUD's gait indicator
      // lie until the panel closed AND a key was pressed. This is a read of
      // existing OS state, not an action, so it is safe while a panel owns
      // the keyboard.
      if (!e.repeat) syncGait(e);
    },

    handleKeyUp(e) {
      // No `!e.repeat` guard: keyup does not repeat.
      syncGait(e);
    },

    dispose() {
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointercancel', onPointerCancel);
      container.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('blur', onWindowBlur);
      // Clear state on teardown too — a remount builds a fresh instance, but
      // leaving `aiming` true here would be the same class of bug the cancel
      // paths above exist to prevent.
      aiming = false;
    },
  };
}
